import { DEFAULT_PARALLEL_BATCH_SIZE } from '@federa/const/config-pagination';
import { positiveIntKnob } from '@federa/const/number-guards';

/** Walk items in fixed-size windows, accumulating per-batch output. Internal helper for processBatches / mapInBatches / mapInBatchesSettled. */
async function reduceBatches<T, R>(
	items: readonly T[],
	size: number,
	onBatch: (batch: readonly T[], startIndex: number) => Promise<readonly R[]>,
): Promise<R[]> {
	const batchIndices = Array.from({ length: Math.ceil(items.length / size) }, (_, k) => k * size);
	return batchIndices.reduce<Promise<R[]>>(async (accP, startIndex) => {
		const acc = await accP;
		const batch = items.slice(startIndex, startIndex + size);
		const batchResults = await onBatch(batch, startIndex);
		acc.push(...batchResults);
		return acc;
	}, Promise.resolve([]));
}

export async function processBatches<T, R>(
	items: T[],
	batchSize: number,
	processBatch: (batch: T[], startIndex: number) => Promise<R>,
): Promise<R[]> {
	return reduceBatches<T, R>(items, batchSize, async (batch, startIndex) => [await processBatch(batch as T[], startIndex)]);
}

/** Map items in parallel with concurrency control. Promise.all per batch -- if any item throws, the batch rejects. */
export async function mapInBatches<T, R>(items: readonly T[], batchSize: number, mapper: (item: T) => Promise<R>): Promise<R[]> {
	const size = positiveIntKnob(batchSize, { fallback: DEFAULT_PARALLEL_BATCH_SIZE });
	return reduceBatches<T, R>(items, size, (batch) => Promise.all(batch.map(mapper)));
}

/** Map items with concurrency control, preserving settled semantics: Promise.allSettled per batch so no single rejection aborts the batch. */
export async function mapInBatchesSettled<T, R>(
	items: readonly T[],
	batchSize: number,
	mapper: (item: T) => Promise<R>,
): Promise<PromiseSettledResult<R>[]> {
	const size = positiveIntKnob(batchSize, { fallback: DEFAULT_PARALLEL_BATCH_SIZE });
	return reduceBatches<T, PromiseSettledResult<R>>(items, size, (batch) => Promise.allSettled(batch.map(mapper)));
}
